import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import DashboardNav from '@/components/DashboardNav';
import FileManagerPanel from '@/components/FileManagerPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Users, BookOpen, Trash2, Copy, UserPlus, GraduationCap, Shield, LogOut, FolderOpen, ImagePlus, ImageOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, token, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isAdmin = user?.role === 'admin';
  const isTeacher = user?.role === 'teacher';
  const isStudent = user?.role === 'student';

  const [classes, setClasses] = useState([]);
  const [users, setUsers] = useState([]);
  const [showCreateClass, setShowCreateClass] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const [className, setClassName] = useState('');
  const [classLevel, setClassLevel] = useState('');
  const [classDesc, setClassDesc] = useState('');
  const [newUser, setNewUser] = useState({ username: '', password: '', first_name: '', last_name: '', email: '', role: 'student' });
  const [createdCreds, setCreatedCreds] = useState(null);
  const [showLangDialog, setShowLangDialog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [classRes, userRes] = await Promise.all([
        axios.get(`${API}/classes`, headers),
        (isAdmin || isTeacher) ? axios.get(`${API}/users`, headers) : Promise.resolve({ data: [] })
      ]);
      setClasses(classRes.data);
      setUsers(userRes.data);
    } catch (err) {
      console.error(err);
    }
  }, [token, isAdmin, isTeacher]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const hasChosen = localStorage.getItem('ae_lang_chosen');
    if (!hasChosen) setShowLangDialog(true);
  }, []);

  const handleCreateClass = async () => {
    try {
      await axios.post(`${API}/classes`, { name: className, level: classLevel, description: classDesc }, headers);
      toast.success(t('success'));
      setShowCreateClass(false);
      setClassName(''); setClassLevel(''); setClassDesc('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const handleCreateUser = async () => {
    try {
      const res = await axios.post(`${API}/users`, newUser, headers);
      setCreatedCreds({ username: res.data.username, password: res.data.password });
      toast.success(t('success'));
      setNewUser({ username: '', password: '', first_name: '', last_name: '', email: '', role: 'student' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const handleDeleteClass = async (classId) => {
    try {
      await axios.delete(`${API}/classes/${classId}`, headers);
      toast.success(t('success'));
      fetchData();
    } catch (err) { toast.error(t('error')); }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, headers);
      toast.success(t('success'));
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const handleVerifyUserEmail = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}/verify-manually`, {}, headers);
      toast.success('Email verificado manualmente');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || t('error')); }
  };

  const handleLangSelect = (lang) => {
    localStorage.setItem('ae_lang_chosen', 'true');
    setShowLangDialog(false);
  };

  const levelColors = {
    'Principiante': 'bg-emerald-100 text-emerald-700',
    'Intermedio': 'bg-amber-100 text-amber-700',
    'Avanzado': 'bg-red-100 text-red-700',
    'Beginner': 'bg-emerald-100 text-emerald-700',
    'Intermediate': 'bg-amber-100 text-amber-700',
    'Advanced': 'bg-red-100 text-red-700',
  };

  // iter72.7 · Per-level accent: top stripe + soft card background (visual only).
  const levelTheme = {
    'Principiante': { stripe: '#2563eb', bg: '#eff6ff' },
    'Beginner': { stripe: '#2563eb', bg: '#eff6ff' },
    'Intermedio': { stripe: '#ea580c', bg: '#fff7ed' },
    'Intermediate': { stripe: '#ea580c', bg: '#fff7ed' },
    'Avanzado': { stripe: '#7c3aed', bg: '#f5f3ff' },
    'Advanced': { stripe: '#7c3aed', bg: '#f5f3ff' },
  };
  const defaultTheme = { stripe: '#059669', bg: '#ecfdf5' };

  // iter75 · Class cover upload (admin + the class's teacher).
  const coverInputRef = React.useRef(null);
  const [coverTargetId, setCoverTargetId] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);

  const handleCoverPick = (classId) => {
    setCoverTargetId(classId);
    coverInputRef.current?.click();
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !coverTargetId) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecciona un archivo de imagen');
      return;
    }
    setUploadingCover(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const up = await axios.post(`${API}/files/upload`, form, headers);
      await axios.put(`${API}/classes/${coverTargetId}`, { cover_filename: up.data.filename }, headers);
      toast.success('Portada actualizada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la portada');
    } finally {
      setUploadingCover(false);
      setCoverTargetId(null);
    }
  };

  const removeCover = async (classId) => {
    try {
      await axios.put(`${API}/classes/${classId}`, { cover_filename: '' }, headers);
      toast.success('Portada quitada');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo quitar la portada');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <DashboardNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="mb-8 animate-slide-up">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Montserrat' }}>
                {t('welcome')}, {user?.first_name}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="capitalize">
                  {user?.role === 'admin' ? <Shield className="w-3 h-3 mr-1" /> : user?.role === 'teacher' ? <BookOpen className="w-3 h-3 mr-1" /> : <GraduationCap className="w-3 h-3 mr-1" />}
                  {t(user?.role || '')}
                </Badge>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="outline"
              onClick={() => { logout(); navigate('/'); }}
              className="rounded-xl gap-2 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600"
            >
              <LogOut className="w-4 h-4" /> {t('logout')}
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        {!isStudent && !showFileManager && (
          <div className="flex flex-wrap gap-3 mb-8 animate-slide-up stagger-1">
            <Button data-testid="create-class-btn" onClick={() => setShowCreateClass(true)} className="rounded-xl gap-2 bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4" /> {t('create_class')}
            </Button>
            <Button data-testid="create-user-btn" onClick={() => { setShowCreateUser(true); setCreatedCreds(null); }} variant="outline" className="rounded-xl gap-2">
              <UserPlus className="w-4 h-4" /> {t('create_user')}
            </Button>
            <Button data-testid="file-manager-btn" onClick={() => setShowFileManager(true)} variant="outline" className="rounded-xl gap-2 border-blue-200 text-blue-600 hover:bg-blue-50">
              <FolderOpen className="w-4 h-4" /> Gestión de Archivos
            </Button>
          </div>
        )}

        {/* File Manager Panel */}
        {showFileManager && !isStudent ? (
          <FileManagerPanel onBack={() => setShowFileManager(false)} />
        ) : (
        <>
        {/* Classes Grid */}
        <div className="mb-8">
          <input
            ref={coverInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCoverUpload}
            data-testid="class-cover-input"
          />
          <h3 className="text-xl font-bold text-slate-700 mb-4" style={{ fontFamily: 'Montserrat' }}>{t('my_classes')}</h3>
          {classes.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-400">{t('no_classes')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {classes.map((cls, i) => {
                const theme = levelTheme[cls.level] || defaultTheme;
                return (
                <Card
                  key={cls.id}
                  data-testid={`class-card-${cls.id}`}
                  className="content-card cursor-pointer rounded-xl hover:shadow-md transition-all animate-slide-up overflow-hidden"
                  style={{
                    animationDelay: `${i * 0.1}s`,
                    backgroundColor: theme.bg,
                    borderTop: `5px solid ${theme.stripe}`,
                  }}
                  onClick={() => navigate(`/class/${cls.id}`)}
                >
                  {cls.cover_filename && (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${cls.cover_filename}`}
                      alt={cls.name}
                      className="w-full h-32 object-cover"
                      data-testid={`class-cover-${cls.id}`}
                    />
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Montserrat' }}>{cls.name}</CardTitle>
                      {!isStudent && (
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`class-cover-btn-${cls.id}`}
                            title={cls.cover_filename ? 'Cambiar portada' : 'Subir portada'}
                            onClick={(e) => { e.stopPropagation(); handleCoverPick(cls.id); }}
                            disabled={uploadingCover}
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                          >
                            <ImagePlus className="w-4 h-4" />
                          </button>
                          {cls.cover_filename && (
                            <button
                              data-testid={`class-cover-remove-${cls.id}`}
                              title="Quitar portada"
                              onClick={(e) => { e.stopPropagation(); removeCover(cls.id); }}
                              className="text-slate-400 hover:text-amber-600 transition-colors p-1"
                            >
                              <ImageOff className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            data-testid={`delete-class-${cls.id}`}
                            onClick={(e) => { e.stopPropagation(); handleDeleteClass(cls.id); }}
                            className="text-slate-400 hover:text-red-500 transition-colors p-1"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {cls.level && (
                      <Badge className={`mb-2 ${levelColors[cls.level] || 'bg-slate-100 text-slate-600'}`}>
                        {cls.level}
                      </Badge>
                    )}
                    {cls.description && <p className="text-sm text-slate-700 mb-3 line-clamp-2">{cls.description}</p>}
                    <div className="flex items-center text-xs text-slate-600 gap-1">
                      <Users className="w-3 h-3" />
                      <span>{cls.member_count || 0} {t('members')}</span>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Users Section - Admin/Teacher */}
        {(isAdmin || isTeacher) && users.length > 0 && (
          <div className="mb-8">
            <h3 className="text-xl font-bold text-slate-700 mb-4" style={{ fontFamily: 'Montserrat' }}>{t('manage_users')}</h3>
            <div className="bg-white rounded-xl border overflow-hidden">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('first_name')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('username')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('email')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('user_role')}</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-600">Estado Email</th>
                    {(isAdmin || isTeacher) && <th className="text-left px-4 py-3 font-semibold text-slate-600">{t('actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.id !== user?.id).map(u => (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-3">{u.first_name} {u.last_name}</td>
                      <td className="px-4 py-3 text-slate-500">{u.username}</td>
                      <td className="px-4 py-3 text-slate-500">{u.email}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className="capitalize">{t(u.role)}</Badge></td>
                      <td className="px-4 py-3">
                        {u.email_verified ?
                          <Badge className="bg-emerald-100 text-emerald-700">{t('validated')}</Badge> :
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{t('email_not_verified')}</Badge>
                            {isAdmin && (
                              <button
                                onClick={() => handleVerifyUserEmail(u.id)}
                                className="text-xs text-blue-500 hover:text-blue-700 underline"
                                title="Verificar manualmente"
                              >
                                Verificar
                              </button>
                            )}
                          </div>
                        }
                      </td>
                      {(isAdmin || isTeacher) && (
                        <td className="px-4 py-3">
                          {/* Teachers can only delete students; admins can delete anyone */}
                          {(isAdmin || (isTeacher && u.role === 'student')) && (
                            <button
                              data-testid={`delete-user-${u.id}`}
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors p-1"
                              title="Eliminar usuario"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </>
        )}
      </main>

      {/* Language Selection Dialog */}
      <Dialog open={showLangDialog} onOpenChange={setShowLangDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl" style={{ fontFamily: 'Montserrat' }}>{t('select_language')}</DialogTitle>
          </DialogHeader>
          <div className="flex gap-4 pt-4">
            <Button data-testid="select-spanish-btn" onClick={() => { handleLangSelect('es'); }} variant="outline" className="flex-1 h-20 rounded-xl text-lg font-bold flex-col gap-1" style={{ fontFamily: 'Montserrat' }}>
              <span className="text-2xl">ES</span>
              <span className="text-xs text-slate-500">{t('spanish')}</span>
            </Button>
            <Button data-testid="select-english-btn" onClick={() => { handleLangSelect('en'); }} variant="outline" className="flex-1 h-20 rounded-xl text-lg font-bold flex-col gap-1" style={{ fontFamily: 'Montserrat' }}>
              <span className="text-2xl">EN</span>
              <span className="text-xs text-slate-500">{t('english')}</span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Class Dialog */}
      <Dialog open={showCreateClass} onOpenChange={setShowCreateClass}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>{t('create_class')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label>{t('class_name')}</Label><Input data-testid="class-name-input" value={className} onChange={e => setClassName(e.target.value)} placeholder={t('class_name')} /></div>
            <div>
              <Label>{t('class_level')}</Label>
              <Select value={classLevel} onValueChange={setClassLevel}>
                <SelectTrigger data-testid="class-level-select"><SelectValue placeholder={t('class_level')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Principiante">{t('level_beginner')}</SelectItem>
                  <SelectItem value="Intermedio">{t('level_intermediate')}</SelectItem>
                  <SelectItem value="Avanzado">{t('level_advanced')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>{t('class_description')}</Label><Textarea data-testid="class-desc-input" value={classDesc} onChange={e => setClassDesc(e.target.value)} placeholder={t('class_description')} /></div>
            <Button data-testid="create-class-submit" onClick={handleCreateClass} className="w-full">{t('create')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>{t('create_user')}</DialogTitle>
          </DialogHeader>
          {createdCreds ? (
            <div className="space-y-4 pt-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                <p className="font-semibold text-emerald-700 mb-2">Credenciales creadas:</p>
                <p className="text-sm"><strong>{t('username')}:</strong> {createdCreds.username}</p>
                <p className="text-sm"><strong>{t('password')}:</strong> {createdCreds.password}</p>
              </div>
              <Button onClick={() => { const ta = document.createElement('textarea'); ta.value = `Usuario: ${createdCreds.username}\nContrasena: ${createdCreds.password}`; ta.style.position = 'fixed'; ta.style.opacity = '0'; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); toast.success(t('link_copied')); }} variant="outline" className="w-full gap-2">
                <Copy className="w-4 h-4" /> {t('copy_link')}
              </Button>
              <Button onClick={() => { setShowCreateUser(false); setCreatedCreds(null); }} className="w-full">{t('close')}</Button>
            </div>
          ) : (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('first_name')}</Label><Input data-testid="user-firstname-input" value={newUser.first_name} onChange={e => setNewUser({...newUser, first_name: e.target.value})} /></div>
                <div><Label>{t('last_name')}</Label><Input data-testid="user-lastname-input" value={newUser.last_name} onChange={e => setNewUser({...newUser, last_name: e.target.value})} /></div>
              </div>
              <div><Label>{t('username')}</Label><Input data-testid="user-username-input" value={newUser.username} onChange={e => setNewUser({...newUser, username: e.target.value})} /></div>
              <div><Label>{t('password')}</Label><Input data-testid="user-password-input" type="password" value={newUser.password} onChange={e => setNewUser({...newUser, password: e.target.value})} /></div>
              <div><Label>{t('email')}</Label><Input data-testid="user-email-input" type="email" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} /></div>
              {isAdmin && (
                <div>
                  <Label>{t('user_role')}</Label>
                  <Select value={newUser.role} onValueChange={v => setNewUser({...newUser, role: v})}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="student">{t('student')}</SelectItem>
                      <SelectItem value="teacher">{t('teacher')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button data-testid="create-user-submit" onClick={handleCreateUser} className="w-full">{t('create')}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
